import { fragmentUser } from "@saleor/fragments/auth";
import { accountErrorFragment } from "@saleor/fragments/errors";
import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import { TypedMutation } from "../mutations";
import {
  ContactUsEmail,
  ContactUsEmailVariables
} from "./types/ContactUsEmail";
import { MfaSetupLater, MfaSetupLaterVariables } from "./types/MfaSetupLater";
import { OTPGenerate, OTPGenerateVariables } from "./types/OTPGenerate";
import { OTPVerify, OTPVerifyVariables } from "./types/OTPVerify";
import {
  RequestPasswordReset,
  RequestPasswordResetVariables
} from "./types/RequestPasswordReset";
import { SellerCreate, SellerCreateVariables } from "./types/SellerCreate";
import { SetPassword, SetPasswordVariables } from "./types/SetPassword";

export const tokenAuthMutation = gql`
  ${fragmentUser}
  ${pageInfoFragment}
  mutation TokenAuth(
    $email: String!
    $password: String!
    $after: String
    $before: String
    $last: Int
  ) {
    tokenCreate(email: $email, password: $password) {
      errors: accountErrors {
        code
        field
        message
        user {
          id
          email
        }
      }
      csrfToken
      token
      user {
        ...User
        malls(after: $after, before: $before, first: 100, last: $last) {
          edges {
            node {
              id
              name
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
      data {
        id
        codeValidity
        resendCooldown
        createdAt
        lockedAt
        lockoutPeriod
        statusCode
        messageCode
        mfaAttempts
        maxAttempts
      }
    }
  }
`;

export const tokenVerifyMutation = gql`
  ${fragmentUser}
  ${pageInfoFragment}
  mutation VerifyToken(
    $token: String!
    $isSwitching: Boolean
    $after: String
    $before: String
    $last: Int
  ) {
    tokenVerify(token: $token, isSwitching: $isSwitching) {
      payload
      data {
        id
        codeValidity
        resendCooldown
        createdAt
        lockedAt
        lockoutPeriod
        statusCode
        messageCode
        mfaAttempts
        maxAttempts
      }
      user {
        ...User
        malls(after: $after, before: $before, first: 100, last: $last) {
          edges {
            node {
              id
              name
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
    }
  }
`;

export const tokenRefreshMutation = gql`
  mutation RefreshToken($token: String!) {
    tokenRefresh(csrfToken: $token) {
      token
    }
  }
`;

export const requestPasswordReset = gql`
  ${accountErrorFragment}
  mutation RequestPasswordReset($email: String!, $redirectUrl: String!) {
    requestPasswordReset(email: $email, redirectUrl: $redirectUrl) {
      errors: accountErrors {
        ...AccountErrorFragment
      }
    }
  }
`;
export const RequestPasswordResetMutation = TypedMutation<
  RequestPasswordReset,
  RequestPasswordResetVariables
>(requestPasswordReset);

export const setPassword = gql`
  ${accountErrorFragment}
  ${fragmentUser}
  mutation SetPassword($email: String!, $password: String!, $token: String!) {
    setPassword(email: $email, password: $password, token: $token) {
      errors: accountErrors {
        ...AccountErrorFragment
      }
      csrfToken
      refreshToken
      token
      user {
        ...User
      }
    }
  }
`;
export const SetPasswordMutation = TypedMutation<
  SetPassword,
  SetPasswordVariables
>(setPassword);

export const sellerCreate = gql`
  mutation SellerCreate($input: StaffCreateInput!) {
    sellerCreate(input: $input) {
      user {
        id
        firstName
        lastName
        email
      }
      errors: staffErrors {
        field
        message
      }
    }
  }
`;
export const SellerCreateMutation = TypedMutation<
  SellerCreate,
  SellerCreateVariables
>(sellerCreate);

export const OTPVerify_ = gql`
  mutation OTPVerify($input: OTPVerifyInput!) {
    otpVerify(input: $input) {
      user {
        id
        email
        firstName
        lastName
        isActive
      }
      otp {
        id
        user {
          id
          firstName
          lastName
        }
        dateGenerated
        dateVerified
        attempt
        status
      }
      errors: otpErrors {
        field
        message
        code
      }
    }
  }
`;
export const OTPVerifyMutation = TypedMutation<OTPVerify, OTPVerifyVariables>(
  OTPVerify_
);

export const OTPGenerate_ = gql`
  mutation OTPGenerate($input: OTPGenerateInput!) {
    otpGenerate(input: $input) {
      otp {
        id
        user {
          id
          firstName
          lastName
        }
        dateGenerated
        dateVerified
        attempt
        status
      }
      errors: otpErrors {
        field
        message
        code
      }
    }
  }
`;
export const OTPGenerateMutation = makeMutation<
  OTPGenerate,
  OTPGenerateVariables
>(OTPGenerate_);

export const contactUsEmail = gql`
  mutation ContactUsEmail($input: ContactUsEmailInput!) {
    contactUsEmail(input: $input) {
      status
      contactUsError {
        field
        message
      }
    }
  }
`;
export const ContactUsEmailMutation = TypedMutation<
  ContactUsEmail,
  ContactUsEmailVariables
>(contactUsEmail);

export const mfaSetupLaterMutation = gql`
  ${fragmentUser}
  ${pageInfoFragment}
  mutation MfaSetupLater(
    $email: String!
    $after: String
    $before: String
    $last: Int
  ) {
    mfaSetupLater(email: $email) {
      errors: accountErrors {
        code
        field
        message
        user {
          id
          email
        }
      }
      csrfToken
      token
      user {
        ...User
        malls(after: $after, before: $before, first: 100, last: $last) {
          edges {
            node {
              id
              name
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
    }
  }
`;

export const MfaSetupLaterMutation = TypedMutation<
  MfaSetupLater,
  MfaSetupLaterVariables
>(mfaSetupLaterMutation);
